import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { LoginI } from '@bodyanalytics/data-models-ui';
import { noMarkupValidator } from '@bodyanalytics/ui-core';

/**
 * Presentational sign-in form for the redesigned login page (Portal — Sign in,
 * Quiet Authority). Two render modes, chosen by the smart page via
 * `embeddedEnabled`:
 *
 * - embedded (`true`): real email + password fields posting to the ROPG proxy
 *   (`credentialsSubmit`), with a "Forgot password?" escape hatch.
 * - redirect (`false`, the flag-off default): the same layout with the fields
 *   replaced by a single CTA that hands off to Auth0 Universal Login
 *   (`redirectLogin`) — no decorative password field pretending to work.
 *
 * `.abx-login__cta` stays on both primary buttons — the calendar e2e suite
 * clicks that class to start a login.
 */
@Component({
  selector: 'bodyanalytics-embedded-login-form',
  templateUrl: './embedded-login-form.component.html',
  styleUrls: ['./embedded-login-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule],
})
export class EmbeddedLoginFormComponent {
  embeddedEnabled = input(false);
  pending = input(false);
  errorMessage = input<string | null>(null);
  showCreateAccount = input(true);

  credentialsSubmit = output<LoginI>();
  redirectLogin = output<void>();
  forgotPassword = output<string>();
  createAccount = output<void>();

  protected readonly loginForm = new FormGroup({
    emailAddress: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.email, noMarkupValidator],
    }),
    password: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
  });

  protected submit(): void {
    if (this.pending()) {
      return;
    }
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }
    this.credentialsSubmit.emit({
      emailAddress: this.loginForm.getRawValue().emailAddress,
      password: this.loginForm.getRawValue().password,
    });
  }

  protected onForgotPassword(): void {
    this.forgotPassword.emit(this.loginForm.getRawValue().emailAddress);
  }

  protected showError(controlName: 'emailAddress' | 'password'): boolean {
    const control = this.loginForm.controls[controlName];
    return control.invalid && control.touched;
  }
}
